import { useEffect, useMemo, useCallback } from 'react';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { useSiteUserQueriesSlice } from '@restore/siteUserQueries/siteUserQueries.slice';
import { QueriesFilters } from 'src/components/Queries/QueriesFilters';
import { useNavigate, useParams } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { twJoin } from '@utils/twMerge';
import { SiteType } from '@dynamika-shared/types/site';
import { routesPath } from '@router/routes';
import { useQueriesColumns } from '@components/Queries/useQueriesColumns';
import { useSiteUserQueryHandlers } from '@components/Queries/useSiteUserQueryHandlers';
import { QueryItem } from '@restore/queries/queries.types';
import {
  useMakePagination,
  usePaginationAndSortSlice,
} from '@restore/paginationAndSort/paginationAndSort.slice';
import { ListName } from '@restore/paginationAndSort/paginationAndSort.types';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';

export const SiteUserQueries = () => {
  const navigate = useNavigate();
  const { id: clinicalTrialId, siteId } = useParams<{
    id: string;
    siteId: string;
  }>();
  useBreadcrumbs([['Site User Queries', 'subjectQueries']], [clinicalTrialId!, siteId!]);

  const [
    loading,
    data,
    fetchQueries,
    filters,
    setFilters,
    currentQueryUpdating,
    updateCurrentQuery,
  ] = useSiteUserQueriesSlice((state, actions) => [
    state.loading,
    state.data,
    actions.fetchSiteUserQueries,
    state.filters,
    actions.setFilters,
    state.currentQueryUpdating,
    actions.updateCurrentQuery,
  ]);
  const [contextData] = useClinicalTrialContextSlice(state => [
    state.data,
    state.loading,
  ]);

  const { handlers } = useSiteUserQueryHandlers({
    ctId: clinicalTrialId as string,
    updating: currentQueryUpdating,
    updateCurrentQuery,
  });
  const columns = useQueriesColumns(handlers);
  const tableData = useMemo(() => data?.content ?? [], [data]);

  const [pagState, pagActions] = usePaginationAndSortSlice((state, actions) => [
    state.lists.siteUserQueries,
    actions
  ]);

  const pagination = useMakePagination(
    'siteUserQueries' as ListName,
    !!tableData.length,
    () => fetchQueries({ clinicalTrialId: clinicalTrialId!, siteId: siteId! })
  );

  const onSort = useCallback((
    columnName: string,
    sortState?: string
  ) => {
    const newSortBy = sortState ? `${columnName},${sortState}` : undefined;
    pagActions.setSortBy({
      listName: 'siteUserQueries' as ListName,
      newSortBy
    });
    fetchQueries({ clinicalTrialId: clinicalTrialId!, siteId: siteId! });
  }, [pagActions.setSortBy, fetchQueries, clinicalTrialId, siteId]);

  useEffect(() => {
    if (clinicalTrialId && siteId) {
      pagActions.resetListStateByName({ listName: 'siteUserQueries' as ListName });
      fetchQueries({ clinicalTrialId, siteId });
    }
  }, [filters?.status, filters?.patientCode, clinicalTrialId, siteId]);

  const tabsItems = useMemo(
    () =>
      contextData?.affiliatedSiteList.map((site: SiteType) => {
        return {
          key: site.id,
          label: (
            <TruncatedText
              text={site.code}
              truncate
              tooltip
              className={twJoin(
                'text-1624 text-dimGray font-medium max-w-[15rem]',
                site.id === siteId && 'text-dodgerBlue'
              )}
            />
          ),
          children: null,
        };
      }),
    [contextData?.affiliatedSiteList, siteId]
  );

  const handleTabChange = useCallback(
    (key: string) => {
      if (!clinicalTrialId) return;
      navigate(`${routesPath.subjectQueriesBase}/${clinicalTrialId}/${key}`);
    },
    [navigate, clinicalTrialId]
  );

  return (
    <div>
      <div className='flex items-center justify-between pb-2 pt-3'>
        <div className='text-12 flex items-center'>
          {contextData?.configuration?.name && (
            <TruncatedText
              text={contextData.configuration.name}
              truncate
              tooltip
              className={
                'mr-1 text-6xl font-medium max-w-[50rem] leading-[55px]'
              }
            />
          )}
        </div>
      </div>

      <div>
        <Tabs
          defaultActiveKey={siteId}
          activeKey={siteId}
          items={tabsItems}
          onChange={handleTabChange}
          blocked={false}
        />
      </div>

      <QueriesFilters
        filters={filters}
        setFilters={setFilters}
        searchKey='patientCode'
      />
      <Table
        loading={loading}
        columns={columns}
        data={tableData}
        totalItems={data?.totalElements ?? 0}
        pagination={pagination}
        onSort={onSort}
        sortState={pagState.sortBy}
        onRowClick={row => {
          const queryId = (row as QueryItem)?.id;
          if (!clinicalTrialId || !queryId || !siteId) return;
          const url =
            generatePath(routesPath.subjectQuery, {
              id: clinicalTrialId,
              queryId,
            }) + `?siteId=${siteId}`;
          navigate(url);
        }}
      />
    </div>
  );
};
